/* 20261105 신규: 웹 헤더의 틀(레이아웃)을 디자이너 파일({landing}/header.js)로 표시
   - header.js 의 html 을 #header 안에 넣고, data-hc-slot="logo" / data-hc-slot="menu" 자리에
     기존 로고(WebHeader)와 메뉴 영역(UserInfoContainer: 로그인 전/후 메뉴, Login 버튼, 프로필)을 그대로 끼워 넣음 (React portal)
   - 로그인 전 / 로그인 후 / 관리자 화면이 같은 틀과 같은 header.css 를 사용
   - 틀 바깥 요소에 data-hc-login="Y|N" (로그인 상태)을 붙여 header.css 에서 로그인 전/후를 구분할 수 있게 함
   - 로컬 서버는 클라우드 파일을 불러오므로(다른 도메인) fetch 대신 <script> 로 읽음 (window.HICLASS_LANDING_HEADER)
   - header.js 가 없거나, 읽지 못하거나, 필수 자리(menu)가 없으면 기존 헤더(fallback)를 그대로 표시 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { getLandingBaseUrl, LANDING_CACHE_KEY } from "../../lib/LandingPath";

const LANDING_HEADER_GLOBAL = "HICLASS_LANDING_HEADER";     // header.js 에서 선언하는 전역 변수 이름
const LANDING_HEADER_TIMEOUT_MS = 4000;                     // 이 시간 안에 못 읽으면 기존 헤더 유지
const LANDING_PATH_PLACEHOLDER = /\{\{LANDING\}\}/g;        // 틀 안에서 디자이너 폴더 주소로 바꿔 줄 표시

let headerLoadPromise = null;   // 앱 전체에서 header.js 는 한 번만 읽음 (화면 이동 시 재사용)

// 20261105 header.js 읽기 (<script> 로드)
const loadLandingHeader = () => {
    if (window[LANDING_HEADER_GLOBAL]) {
        return Promise.resolve(window[LANDING_HEADER_GLOBAL]);
    }

    if (!headerLoadPromise) {
        headerLoadPromise = new Promise(resolve => {
            let isDone = false;
            const finish = () => {
                if (isDone) return;
                isDone = true;
                resolve(window[LANDING_HEADER_GLOBAL] || null);
            };

            const script = document.createElement("script");
            script.src = getLandingBaseUrl() + "header.js?t=" + LANDING_CACHE_KEY;
            script.async = true;
            script.onload = finish;
            script.onerror = finish;

            setTimeout(finish, LANDING_HEADER_TIMEOUT_MS);
            document.head.appendChild(script);
        });
    }

    return headerLoadPromise;
};

const WebHeaderLandingTemplate = ({ logo, menu, fallback, isLoginYn }) => {
    const containerRef = useRef(null);

    // 20261105 현재 앱 언어 (브라우저 언어 기준, 예: "en-US" → "en")
    const { i18n } = useTranslation();
    const lang = String(i18n.language || "ko").substring(0, 2).toLowerCase();

    // undefined: 읽는 중 / null: 사용 불가 / object: header.js 내용
    const [landingHeader, setLandingHeader] = useState(() => window[LANDING_HEADER_GLOBAL] || undefined);
    const [slots, setSlots] = useState(null);       // null: 준비 전 / "invalid": 틀 오류 / { logo, menu }: 슬롯 요소

    useEffect(() => {
        let isCancelled = false;

        if (landingHeader === undefined) {
            loadLandingHeader().then(header => {
                if (!isCancelled) {
                    setLandingHeader(header ? header : null);
                }
            });
        }

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 20261105 틀 HTML 준비 ({{LANDING}} → 디자이너 폴더 주소)
    const templateHtml = useMemo(() => {
        if (!landingHeader || typeof landingHeader !== "object" || typeof landingHeader.html !== "string" || landingHeader.html.trim() === "") {
            return null;
        }

        return landingHeader.html.trim().replace(LANDING_PATH_PLACEHOLDER, getLandingBaseUrl());
    }, [landingHeader]);

    // 20261105 틀을 넣고 슬롯 위치 찾기 (화면에 그리기 전에 처리해 깜빡임 방지)
    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container || !templateHtml) {
            return;
        }

        container.innerHTML = templateHtml;     // 우리 서버(header.js)의 틀만 사용, 틀 안의 <script> 는 실행되지 않음

        // 틀 안 문구 다국어 (data-i18n="키" → header.js 의 i18n[언어][키])
        const i18nSet = landingHeader.i18n && typeof landingHeader.i18n === "object" ? landingHeader.i18n : {};
        const dict = i18nSet[lang] || i18nSet.ko || {};

        container.querySelectorAll("[data-i18n]").forEach(node => {
            const value = dict[node.getAttribute("data-i18n")];
            if (typeof value === "string") {
                node.textContent = value;
            }
        });

        const menuSlot = container.querySelector('[data-hc-slot="menu"]');
        const logoSlot = container.querySelector('[data-hc-slot="logo"]');

        if (!menuSlot) {
            console.log("WebHeaderLandingTemplate - header.js 에 data-hc-slot=\"menu\" 가 없어 기존 헤더를 표시합니다.");
            setSlots("invalid");
            return;
        }

        setSlots({ logo: logoSlot, menu: menuSlot });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateHtml, lang]);

    // 읽는 중이거나 사용할 수 없으면 기존 헤더
    // (key 를 서로 다르게 줘서 틀 ↔ 기존 헤더 전환 시 DOM 을 재사용하지 않도록 함 - 틀 잔여물 방지)
    if (landingHeader === undefined || !templateHtml || slots === "invalid") {
        return <React.Fragment key="original-header">{fallback}</React.Fragment>;
    }

    return (
        <div className="d-flex w-100" ref={containerRef} key="landing-header" data-hc-login={isLoginYn === "Y" ? "Y" : "N"}>
            {slots && slots.logo && logo ? ReactDOM.createPortal(logo, slots.logo) : null}
            {slots && slots.menu ? ReactDOM.createPortal(menu, slots.menu) : null}
        </div>
    );
};

export default WebHeaderLandingTemplate;
